"use client";

import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border rounded-md" style={{
      borderColor: "hsl(45 60% 30%)",
      backgroundColor: "hsl(220 10% 12%)"
    }}>
        <p style={{
        color: "hsl(45 90% 50%)"
      }} className="text-lg font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-2" style={{
        borderColor: "hsl(45 90% 50%)",
        color: "hsl(45 90% 50%)"
      }}>
          Send another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Artist / Band Name
            </FormLabel>
            <FormControl>
              <Input placeholder="The Soundwaves" className="bg-transparent border-0 border-b-2 px-0 focus-visible:ring-0 focus-visible:border-primary transition-[border] duration-200 rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Contact Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="booking@band.com" className="bg-transparent border-0 border-b-2 px-0 focus-visible:ring-0 focus-visible:border-primary transition-[border] duration-200 rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 000-0000" className="bg-transparent border-0 border-b-2 px-0 focus-visible:ring-0 focus-visible:border-primary transition-[border] duration-200 rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Label / Management
            </FormLabel>
            <FormControl>
              <Input placeholder="Independent" className="bg-transparent border-0 border-b-2 px-0 focus-visible:ring-0 focus-visible:border-primary transition-[border] duration-200 rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a session type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Session Type
            </FormLabel>
            <FormControl>
              <select {...field} className="w-full bg-transparent border-0 border-b-2 px-0 py-2 focus:outline-none focus:border-primary transition-[border] duration-200 font-default rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }}>
                <option value="" className="bg-card">Select an option</option>
                <option value="Rehearsal" className="bg-card">Rehearsal Space</option>
                <option value="Live Tracking" className="bg-card">Live Tracking Session</option>
                <option value="Mixing" className="bg-card">Mixing & Mastering</option>
                <option value="Tour Prep" className="bg-card">Tour Pre-Production</option>
                <option value="Other" className="bg-card">Other</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "A brief message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase font-mono" style={{
        letterSpacing: "0.2em",
        color: "hsl(220 5% 50%)"
      }}>
              Gear Requirements
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your backline needs..." rows={4} className="bg-transparent border-0 border-b-2 px-0 focus-visible:ring-0 focus-visible:border-primary transition-[border] duration-200 resize-none rounded-md" style={{
          borderBottomColor: "hsl(220 5% 50%)",
          color: "hsl(0 0% 95%)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="privilege-ack" className="mt-1 rounded-md" style={{
        borderColor: "hsl(45 90% 50%)"
      }} />
        <label htmlFor="privilege-ack" className="text-sm leading-relaxed font-default cursor-pointer" style={{
        color: "hsl(220 5% 70%)"
      }}>
          I agree to the studio house rules and equipment handling policy.
        </label>
      </div>

      {submitError && <div className="p-4 border rounded-md" style={{
      borderColor: "hsl(0 84% 60%)",
      backgroundColor: "hsl(0 84% 60% / 0.08)"
    }}>
          <p className="text-sm" style={{
        color: "hsl(0 84% 60%)"
      }}>
            {submitError}
          </p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="rounded-md px-7 py-6 text-base font-bold shadow-md hover:translate-x-1 hover:translate-y-1 transition-transform" style={{
        backgroundColor: "hsl(45 90% 50%)",
        color: "hsl(220 15% 5%)"
      }}>
          {isSubmitting ? "TRANSMITTING..." : "LOCK IN YOUR SLOT"}
        </Button>
        <p className="mt-3 font-mono text-xs uppercase" style={{
        color: "hsl(45 90% 50%)",
        transform: "rotate(-1deg)",
        transformOrigin: "left center"
      }}>
          ↳ signal strength: high
        </p>
      </div>
    </Form>;
}
export default function LockInYourSlot() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1712" id="lock-in-your-slot" className="relative overflow-hidden bg-background text-foreground py-24 md:py-44">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 bottom-0 w-[42rem] h-[42rem] opacity-10" style={{
      background: "radial-gradient(ellipse 60% 70% at 45% 55%, hsl(45 90% 50%) 0%, hsl(45 60% 30% / 0.6) 40%, transparent 70%)",
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
    }} />

      <div ref={ref} className={`relative max-w-[1320px] mx-auto px-5 md:px-8 lg:px-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <div className="flex items-baseline gap-4 mb-8">
              <span className="font-mono text-xs uppercase" style={{
              letterSpacing: "0.2em",
              color: "hsl(45 90% 50%)"
            }}>
                SECTION_04 // BOOKING
              </span>
              <span className="font-mono text-sm uppercase" style={{
              color: "hsl(220 5% 50%)",
              transform: "rotate(-2deg)"
            }}>
                live_tracking
              </span>
            </div>

            <h2 className="font-default text-4xl md:text-5xl lg:text-7xl leading-[0.9] mb-8 uppercase" style={{
            color: "hsl(0 0% 95%)",
            letterSpacing: "-0.04em",
            fontWeight: 800
          }}>
              Ready to{" "}
              <span className="relative inline-block">
                <span className="text-primary" style={{
                fontSize: "1.05em"
              }}>
                  Crank
                </span>
                <svg className="absolute left-0 -bottom-2 w-full" height="10" viewBox="0 0 120 10" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M0,5 L120,5" fill="none" stroke="hsl(45 90% 50%)" strokeWidth="4" strokeLinecap="square" style={{
                  strokeDasharray: 200,
                  strokeDashoffset: isVisible ? 0 : 200,
                  transition: "stroke-dashoffset 600ms ease-out 400ms"
                }} />
                </svg>
              </span>{" "}
              the volume?
            </h2>

            <p className="text-lg md:text-xl leading-relaxed mb-12 max-w-xl font-default" style={{
            color: "hsl(220 5% 70%)"
          }}>
              Drop your details below to secure your rehearsal or tracking session. We run a tight ship and respond to all inquiries within the hour.
            </p>

            <FormProvider formKey="slot_booking_form_home" formId="slot_booking_form_home" sectionName="reach-out-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="LOCK IN YOUR SLOT" successMessage="Signal received. We've blocked out your request and will confirm your session shortly." errorMessage="Transmission failed. Check your connection or call the studio directly.">
              <ReachOutForm />
            </FormProvider>
          </div>

          <div className="lg:col-span-5">
            <p className="font-mono text-xl md:text-2xl mb-6 inline-block uppercase font-bold" style={{
            color: "hsl(45 90% 50%)",
            transform: "rotate(-3deg)"
          }}>
              Visit the bunker
            </p>

            <div className="relative p-8 md:p-10 shadow-md border-2" style={{
            backgroundColor: "hsl(220 10% 12%)",
            borderColor: "hsl(45 60% 30%)",
            borderRadius: "0px"
          }}>
              <h3 className="font-default text-2xl md:text-3xl mb-8 uppercase" style={{
              color: "hsl(0 0% 95%)",
              fontWeight: 700,
              letterSpacing: "-0.02em"
            }}>
                The Garage Room HQ
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="size-5 mt-1 shrink-0" style={{
                  color: "hsl(45 90% 50%)",
                  strokeWidth: 2
                }} />
                  <address className="not-italic font-mono text-sm leading-relaxed" style={{
                  color: "hsl(0 0% 95%)"
                }}>
                    42 Industrial Way
                    <br />
                    Brooklyn, NY 11211
                  </address>
                </div>

                <Separator style={{
                backgroundColor: "hsl(45 60% 30%)"
              }} />

                <div className="flex items-start gap-4">
                  <Phone className="size-5 mt-1 shrink-0" style={{
                  color: "hsl(45 90% 50%)",
                  strokeWidth: 2
                }} />
                  <Link to="tel:+17185550909" className="font-mono text-sm hover:text-primary transition-colors" style={{
                  color: "hsl(0 0% 95%)"
                }}>
                    (718) 555-0909
                  </Link>
                </div>

                <Separator style={{
                backgroundColor: "hsl(45 60% 30%)"
              }} />

                <div className="flex items-start gap-4">
                  <Mail className="size-5 mt-1 shrink-0" style={{
                  color: "hsl(45 90% 50%)",
                  strokeWidth: 2
                }} />
                  <Link to="mailto:loud@thegarageroom.com" className="font-mono text-sm break-all hover:text-primary transition-colors" style={{
                  color: "hsl(0 0% 95%)"
                }}>
                    loud@thegarageroom.com
                  </Link>
                </div>

                <Separator style={{
                backgroundColor: "hsl(45 60% 30%)"
              }} />

                <div className="flex items-start gap-4">
                  <Clock className="size-5 mt-1 shrink-0" style={{
                  color: "hsl(45 90% 50%)",
                  strokeWidth: 2
                }} />
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-xs uppercase" style={{
                  letterSpacing: "0.12em",
                  color: "hsl(0 0% 95%)"
                }}>
                    <span>Daily</span>
                    <span>10am — 2am</span>
                    <span>Lockouts</span>
                    <span>24/7 Access</span>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 size-24 flex items-center justify-center" style={{
              transform: "rotate(12deg)"
            }}>
                <div className="relative size-full flex items-center justify-center shadow-md" style={{
                backgroundColor: "hsl(45 90% 50%)",
                borderRadius: "0px",
                border: "3px solid hsl(220 15% 5%)"
              }}>
                  <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
                    <defs>
                      <path id="seal-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                    </defs>
                    <text style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "8px",
                    fontWeight: "bold",
                    letterSpacing: "0.1em",
                    fill: "hsl(220 15% 5%)"
                  }}>
                      <textPath href="#seal-circle" startOffset="0">
                        · LIVE · LOUD · RAW · EST 2024 ·
                      </textPath>
                    </text>
                  </svg>
                  <span className="font-default text-sm font-black text-center leading-tight relative z-10" style={{
                  color: "hsl(220 15% 5%)"
                }}>
                    GRG
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}